import {useState, useEffect, useCallback} from 'react';

// Detects a waiting service-worker and exposes:
// • hasNewRelease – show toast when true
// • reloadPage    – skip waiting & hard-reload
export default function useServiceWorker() {
  const [waitingWorker, setWaitingWorker] = useState(null);
  const [hasNewRelease, setHasNewRelease] = useState(false);

  const reloadPage = useCallback(() => {
    console.debug('[useServiceWorker] reloadPage invoked', {
      hasWaiting: Boolean(waitingWorker),
    });
    waitingWorker?.postMessage({type: 'SKIP_WAITING'});
    window.location.reload();
  }, [waitingWorker]);

  useEffect(() => {
    if (!('serviceWorker' in navigator)) return;

    const handleRegistration = (reg) => {
      if (!reg) return;

      const flagWaiting = () => {
        setWaitingWorker(reg.waiting);
        setHasNewRelease(Boolean(reg.waiting));
      };

      if (reg.waiting) flagWaiting();
      reg.onupdatefound = () => {
        reg.installing?.addEventListener('statechange', () => {
          if (
            reg.installing?.state === 'installed' &&
            navigator.serviceWorker.controller
          ) {
            flagWaiting();
          }
        });
      };
    };

    navigator.serviceWorker.getRegistration().then(handleRegistration);
  }, []);

  useEffect(() => {
    if (!('serviceWorker' in navigator)) return;

    // Helper – called with the current registration or when one is obtained.
    const listenForWaiting = (registration) => {
      if (!registration) return;

      // If a worker is already waiting when we load, surface it immediately.
      if (registration.waiting) {
        setWaitingWorker(registration.waiting);
        setHasNewRelease(true);
        return;
      }

      // Otherwise watch for new workers reaching the "installed" state.
      registration.onupdatefound = () => {
        const installing = registration.installing;
        if (installing) {
          installing.onstatechange = () => {
            if (
              installing.state === 'installed' &&
              navigator.serviceWorker.controller // page still under old SW
            ) {
              setWaitingWorker(registration.waiting);
              setHasNewRelease(true);
            }
          };
        }
      };
    };

    // Check current registration.
    navigator.serviceWorker.getRegistration().then(listenForWaiting);
  }, []);
  return {hasNewRelease, waitingWorker, reloadPage};
}
